import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../config/api';

// Funzione per ottenere autorizzazioni di fallback minimali in caso di errore critico
const getFallbackAutorizzazioni = () => {
  return {
    clienti: { abilitato: false, sottopagine: {} },
    quotazioni: { abilitato: false, sottopagine: {} },
    'rete-multilivello': { abilitato: false, sottopagine: {} },
    gestione: { abilitato: false, sottopagine: {} },
    documenti: { abilitato: true, sottopagine: { 'tutorial': true } }, // Almeno i tutorial sempre accessibili
    statistiche: { abilitato: false, sottopagine: {} }
  };
};

const useAutorizzazioni = (user) => {
  const [autorizzazioni, setAutorizzazioni] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mappa i ruoli utente ai ruoli del sistema di autorizzazioni
  const mappaRuoloUtente = useCallback((ruoloUtente) => {
    const mappingRuoli = {
      'admin': 'admin',
      'agenzia': 'agenzia',
      'team_leader': 'team_leader',
      'subagente': 'subagente',
      'segnalatore': 'segnalatore',
      // Fallback per ruoli legacy
      'agente': 'subagente',
      'agente_commerciale': 'subagente'
    };
    
    return mappingRuoli[ruoloUtente] || 'segnalatore';
  }, []);

  // Carica le autorizzazioni dal server
  const caricaAutorizzazioni = useCallback(async () => {
    const ruoloUtente = user?.ruolo || user?.role;
    if (!ruoloUtente) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ruoloMappato = mappaRuoloUtente(ruoloUtente);
// console.log(`🔐 Caricamento autorizzazioni per ruolo: ${ruoloUtente} -> ${ruoloMappato}`);
// console.log(`🌐 API_CONFIG.baseURL:`, API_CONFIG.baseURL);
// console.log(`🌐 URL richiesta: /gestione-ruoli/mie-autorizzazioni`);
// console.log(`🔑 Token:`, localStorage.getItem('token') ? 'presente' : 'assente');

      const response = await api.get(`/gestione-ruoli/mie-autorizzazioni`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

// console.log(`📡 Risposta server:`, response.data);

      if (response.data.successo) {
        setAutorizzazioni(response.data.data);
// console.log('✅ Autorizzazioni caricate:', response.data.data);
      } else {
        throw new Error(response.data.error || 'Errore nel caricamento autorizzazioni');
      }
    } catch (error) {
      console.error('❌ Errore caricamento autorizzazioni:', error);
      setError(error.message);
      
      // Fallback: autorizzazioni minimali solo in caso di errore critico
      console.warn('⚠️ Errore nel caricamento autorizzazioni, usando fallback minimale');
      const fallbackAutorizzazioni = getFallbackAutorizzazioni();
      setAutorizzazioni(fallbackAutorizzazioni);
    } finally {
      setLoading(false);
    }
  }, [user?.ruolo, mappaRuoloUtente]);

  // Carica autorizzazioni quando cambia l'utente
  useEffect(() => {
    caricaAutorizzazioni();
  }, [caricaAutorizzazioni]);

  // Verifica se una pagina è abilitata
  const isPaginaAbilitata = useCallback((pagina) => {
    if (!autorizzazioni) return false;
    return autorizzazioni[pagina]?.abilitato || false;
  }, [autorizzazioni]);

  // Verifica se una sottopagina è abilitata
  const isSottopaginaAbilitata = useCallback((pagina, sottopagina) => {
    if (!autorizzazioni) return false;
    const paginaConfig = autorizzazioni[pagina];
    if (!paginaConfig?.abilitato) return false;
    
    // Se non ci sono sottopagine specificate, eredita l'abilitazione della pagina principale
    if (!paginaConfig.sottopagine) return true;
    
    return paginaConfig.sottopagine[sottopagina] || false;
  }, [autorizzazioni]);

  // Verifica accesso completo (pagina + sottopagina)
  const verificaAccesso = useCallback((pagina, sottopagina = null) => {
    if (!autorizzazioni) return false;
    
    const paginaAbilitata = isPaginaAbilitata(pagina);
    if (!paginaAbilitata) return false;
    
    if (sottopagina) {
      return isSottopaginaAbilitata(pagina, sottopagina);
    }
    
    return true;
  }, [autorizzazioni, isPaginaAbilitata, isSottopaginaAbilitata]);

  // Ottieni tutte le sottopagine abilitate per una pagina
  const getSottopagineAbilitate = useCallback((pagina) => {
    if (!autorizzazioni || !isPaginaAbilitata(pagina)) return [];
    
    const paginaConfig = autorizzazioni[pagina];
    if (!paginaConfig.sottopagine) return [];
    
    return Object.entries(paginaConfig.sottopagine)
      .filter(([_, abilitata]) => abilitata)
      .map(([sottopagina, _]) => sottopagina);
  }, [autorizzazioni, isPaginaAbilitata]);

  // Verifica se l'utente è admin
  const isAdmin = useCallback(() => {
    const ruoloUtente = user?.ruolo || user?.role;
    return mappaRuoloUtente(ruoloUtente) === 'admin';
  }, [user?.ruolo, user?.role, mappaRuoloUtente]);

  // Ottieni il livello di accesso (percentuale)
  const getLivelloAccesso = useCallback(() => {
    if (!autorizzazioni) return 0;
    
    const pagineDisponibili = ['clienti', 'quotazioni', 'rete-multilivello', 'gestione', 'documenti', 'statistiche'];
    const pagineAbilitate = pagineDisponibili.filter(pagina => isPaginaAbilitata(pagina));
    
    return Math.round((pagineAbilitate.length / pagineDisponibili.length) * 100);
  }, [autorizzazioni, isPaginaAbilitata]);

  // Verifica se l'utente può accedere a dati specifici
  const puoAccedereADati = useCallback((tipoRisorsa, azione = 'read') => {
    if (!autorizzazioni || !user) return false;
    
    // Logica specifica per tipo di risorsa
    const mappingRisorse = {
      'clienti': { pagina: 'clienti', sottopagina: 'lista-clienti' },
      'lead': { pagina: 'clienti', sottopagina: 'gestione-lead' },
      'pratiche': { pagina: 'quotazioni', sottopagina: 'pratiche' },
      'quotazioni': { pagina: 'quotazioni', sottopagina: 'lista-quotazioni' },
      'veicoli': { pagina: 'quotazioni', sottopagina: 'lista-veicoli' },
      'contratti': { pagina: 'gestione', sottopagina: 'contratti' },
      'provvigioni': { pagina: 'gestione', sottopagina: 'provvigioni-contratti' },
      'statistiche-fatturato': { pagina: 'statistiche', sottopagina: 'statistiche-fatturato' },
      'statistiche-pratiche': { pagina: 'statistiche', sottopagina: 'statistiche-pratiche' },
      'utenti-rete': { pagina: 'rete-multilivello', sottopagina: 'gestione-utenti' },
      'configurazione-ruoli': { pagina: 'rete-multilivello', sottopagina: 'gestione-ruoli' }
    };
    
    const risorsa = mappingRisorse[tipoRisorsa];
    if (!risorsa) {
      console.warn(`🔒 Tipo di risorsa non mappato: ${tipoRisorsa}`);
      return false;
    }
    
    // Per azioni di scrittura, verifica autorizzazioni più stringenti
    if (azione === 'write' || azione === 'delete') {
      // Solo admin può fare tutto
      if (isAdmin()) return true;
      
      // Altri ruoli con limitazioni specifiche
      const ruolo = mappaRuoloUtente(user?.ruolo || user?.role);
      if (ruolo === 'segnalatore' && (azione === 'write' || azione === 'delete')) {
        // Segnalatore può solo leggere
        return false;
      }
    }
    
    return verificaAccesso(risorsa.pagina, risorsa.sottopagina);
  }, [autorizzazioni, user, verificaAccesso, isAdmin, mappaRuoloUtente]);

  // Verifica accesso basato su gerarchia nodi (per futuri sviluppi)
  const puoAccedereDaGerarchia = useCallback((risorsa, targetUserId = null) => {
    if (!user || !autorizzazioni) return false;
    
    // Per ora usa solo le autorizzazioni standard
    // In futuro si può estendere con logica di gerarchia nodi
    const hasBasicAccess = puoAccedereADati(risorsa);
    
    // Se non specificato un utente target, controlla solo accesso base
    if (!targetUserId) return hasBasicAccess;
    
    // Logica futura per gerarchia:
    // - Admin può vedere tutto
    // - Agenzia può vedere i suoi team leader e subagenti
    // - Team leader può vedere solo i suoi subagenti
    // - Subagente/Segnalatore vedono solo i propri dati
    
    if (isAdmin()) return hasBasicAccess;
    
    // Per ora tutti vedono tutto se hanno accesso base
    // TODO: Implementare logica gerarchia nodi
    return hasBasicAccess;
  }, [user, autorizzazioni, puoAccedereADati, isAdmin]);

  return {
    autorizzazioni,
    loading,
    error,
    isPaginaAbilitata,
    isSottopaginaAbilitata,
    verificaAccesso,
    getSottopagineAbilitate,
    isAdmin,
    getLivelloAccesso,
    ricaricaAutorizzazioni: caricaAutorizzazioni,
    ruoloMappato: (user?.ruolo || user?.role) ? mappaRuoloUtente(user?.ruolo || user?.role) : null,
    // Nuove funzioni per protezione dati
    puoAccedereADati,
    puoAccedereDaGerarchia
  };
};

export default useAutorizzazioni; 